import type { DateBasis } from "@repo/app-types/entities/dateBasis";
import {
  ceilToStartOfDay,
  endOfContainingDay,
  type DateRange,
} from "@repo/app-types/entities/dateRange";
import type {
  CoachingGoalCondition,
  CustomFieldCondition,
  OutcomeCondition,
  PlaybookCondition,
  RecordingCountCondition,
  SignalMetadataCondition,
  TotalRecordingDurationCondition,
} from "@repo/app-types/entities/filterConditions";
import {
  selectPopulationFilters,
  toISODateTime,
  type CriteriaFilter,
  type CustomFieldFilter,
  type FirstCallDateFilter,
  type ISODateTime,
  type OutcomeFilter,
  type PlaybookHasResultFilter,
  type PlaybookScope,
  type RecordingCountFilter,
  type SectionFilter,
  type SignalsPageFilter,
  type TeammateGroupFilter,
  type TeamPerformancePageFilter,
  type TotalRecordingDurationFilter,
} from "@repo/app-types/entities/filters";
import type { GroupId } from "@repo/app-types/entities/group";
import type { PlaybookId } from "@repo/app-types/entities/playbook";
import type { SignalId } from "@repo/app-types/entities/signal";
import { DateTime } from "luxon";

/**
 * Shape converter from the FE filter state (luxon `DateRange`,
 * `OutcomeCondition` / `CustomFieldCondition` / `SignalMetadataCondition`
 * objects produced by `useFilters` + `FilterContext`) to the structured
 * `{scope, filters}` wire pair consumed by the page-stats endpoints.
 */
export interface BuildFilterQueryArgsOptions {
  playbookId: PlaybookId;
  dateRange: DateRange;
  dateBasis?: DateBasis;
  // `null` means "no teammate group filter applied" (equivalent to "All teammates"). An empty array is possible but should not happen in practice since it will result in an empty result set.
  teammateGroupIds: readonly GroupId[] | null;
  outcome: OutcomeCondition | null;
  customFields: readonly CustomFieldCondition[];
  totalRecordingDuration: TotalRecordingDurationCondition | null;
  recordingCount?: RecordingCountCondition | null;
  playbookConditions: readonly PlaybookCondition[];
}

export interface BuildSignalFilterQueryArgsOptions extends BuildFilterQueryArgsOptions {
  /** Scopes results to a single signal when set. Drilldown sets this from
   * the URL param; other pages may set it from the `signalId` FilterKey;
   * pages with no signal in scope pass `null`. */
  signalId: SignalId | null;
  signalMetadataFields: readonly SignalMetadataCondition[];
}

/**
 * Universal {scope, filters} pair accepted by every page-stats endpoint.
 * Page-specific builders compose on top of this — they reuse the scope
 * and widen the filter array with their own variants.
 */
export function buildFilterQueryArgs(
  now: DateTime<true>,
  opts: BuildFilterQueryArgsOptions,
): {
  scope: PlaybookScope;
  filters: UniversalFilter[];
} {
  const scope = buildPlaybookScope(
    now,
    opts.playbookId,
    opts.dateRange,
    opts.dateBasis,
  );
  return {
    scope,
    filters: buildUniversalFilters({
      ...opts,
      selectedDateRange: scope.dateRange,
      selectedDateRangeTimeZone: scope.timeZone ?? "UTC",
    }),
  };
}

/**
 * Builds the `PlaybookScope` for a page-stats request: the resolved ISO date
 * range plus the browser's IANA zone, so the server aligns day buckets to the
 * user's calendar. Use this (rather than composing a scope by hand) wherever
 * a scope is built outside {@link buildFilterQueryArgs}.
 */
export function buildPlaybookScope(
  now: DateTime<true>,
  playbookId: PlaybookId,
  dateRange: DateRange,
  dateBasis?: DateBasis,
): PlaybookScope {
  return {
    playbookId,
    dateRange: dateRangeToISORange(now, dateRange),
    dateBasis,
    timeZone: now.zoneName,
  };
}

/**
 * Whether a built filter array holds anything beyond the population selectors
 * ({@link selectPopulationFilters}) — i.e. whether the "N of M" denominator
 * query would actually differ from the list's own query.
 *
 * Pages pair this with `hasNarrowingChanges` from `useFilters`. The two catch
 * different no-op cases and both are needed: a date-range change registers as
 * a change but lands in the shared `PlaybookScope` rather than the filters, and
 * conditions belonging to another playbook are dropped by
 * {@link buildFilterQueryArgs} — either way both queries would be identical and
 * render a useless "45 of 45". Conversely a teammate group carried over from
 * the viewer's saved preference does reach the filters, but is nobody's
 * narrowing choice on this page, which only `hasNarrowingChanges` can tell.
 */
export function checkHasNarrowingFilters(
  filters: readonly { type: string }[],
): boolean {
  return selectPopulationFilters(filters).length !== filters.length;
}

/**
 * Universal filter block shared by every page-stats endpoint: teammate
 * group, outcome, custom fields, total recording duration, recording count,
 * `criteria` / `section` / `playbook_has_result`
 */
type UniversalFilter =
  | TeammateGroupFilter
  | OutcomeFilter
  | CustomFieldFilter
  | TotalRecordingDurationFilter
  | RecordingCountFilter
  | CriteriaFilter
  | SectionFilter
  | PlaybookHasResultFilter
  | FirstCallDateFilter;

function buildUniversalFilters(opts: {
  playbookId: PlaybookId | null;
  teammateGroupIds: readonly GroupId[] | null;
  outcome: OutcomeCondition | null;
  customFields: readonly CustomFieldCondition[];
  totalRecordingDuration: TotalRecordingDurationCondition | null;
  recordingCount?: RecordingCountCondition | null;
  playbookConditions: readonly PlaybookCondition[];
  selectedDateRange?: PlaybookScope["dateRange"];
  selectedDateRangeTimeZone?: string;
}): UniversalFilter[] {
  const filters: UniversalFilter[] = [];

  if (opts.teammateGroupIds !== null) {
    filters.push({ type: "teammate_group", ids: opts.teammateGroupIds });
  }

  if (opts.outcome && opts.outcome.playbookId === opts.playbookId) {
    filters.push({
      type: "outcome",
      id: opts.outcome.outcomeDefinitionId,
      outcomeKind: opts.outcome.outcomeType,
      match: { op: "equals", value: opts.outcome.outcomeValue },
    });
  }

  for (const condition of opts.customFields) {
    if (condition.operator === "has_any_value") {
      filters.push({
        type: "custom_field",
        id: condition.customFieldDefinitionId,
        match: { op: "exists" },
      });
      continue;
    }

    if (condition.operator === "after" || condition.operator === "before") {
      filters.push({
        type: "custom_field",
        id: condition.customFieldDefinitionId,
        match: {
          op: condition.operator,
          fieldType: condition.fieldType,
          value: condition.value,
        },
      });
      continue;
    }

    if (condition.operator === "within_selected_date_range") {
      if (!opts.selectedDateRange) {
        throw new Error(
          "A selected date range is required for the custom field date filter",
        );
      }

      const selectedDateRange = isoDateRangeToCalendarDates(
        opts.selectedDateRange,
        opts.selectedDateRangeTimeZone ?? "UTC",
      );
      filters.push({
        type: "custom_field",
        id: condition.customFieldDefinitionId,
        match: {
          op: "within",
          fieldType: condition.fieldType,
          ...selectedDateRange,
        },
      });
      continue;
    }

    filters.push({
      type: "custom_field",
      id: condition.customFieldDefinitionId,
      match: { op: "equals", value: String(condition.value) },
    });
  }

  if (opts.totalRecordingDuration) {
    filters.push({
      type: "total_recording_duration",
      op: opts.totalRecordingDuration.operator,
      value: opts.totalRecordingDuration.value,
    });
  }

  if (opts.recordingCount) {
    filters.push({
      type: "recording_count",
      op: opts.recordingCount.operator,
      value: opts.recordingCount.value,
    });
  }

  for (const condition of opts.playbookConditions) {
    if (condition.playbookId !== opts.playbookId) continue;

    if ("sectionId" in condition) {
      filters.push({
        type: "section",
        id: condition.sectionId,
        notApplicable: !condition.value,
      });
    } else if ("criterionId" in condition) {
      if (condition.gradingValueType === "yes_no") {
        filters.push({
          type: "criteria",
          id: condition.criterionId,
          score: { valueType: "yes_no", value: condition.value },
        });
      } else {
        filters.push({
          type: "criteria",
          id: condition.criterionId,
          score: {
            valueType: "1_5_score",
            op: condition.operator,
            value: condition.value,
          },
        });
      }
    } else if ("kind" in condition && condition.kind === "has_result") {
      filters.push({ type: "playbook_has_result" });
    }
  }

  return filters;
}

function isoDateRangeToCalendarDates(
  dateRange: PlaybookScope["dateRange"],
  timeZone: string,
): { start: string; end: string } {
  const start = DateTime.fromISO(dateRange.after, { setZone: true }).setZone(
    timeZone,
  );
  const end = DateTime.fromISO(dateRange.before, { setZone: true }).setZone(
    timeZone,
  );

  if (!start.isValid || !end.isValid) {
    throw new Error(
      `Invalid selected date range: ${dateRange.after} - ${dateRange.before}`,
    );
  }

  return {
    start: start.toISODate(),
    end: end.toISODate(),
  };
}

/**
 * Signals variant: adds the `signal` filter (with metadata-field
 * conditions packed as its `metadataFilters`) on top of the universal
 * filters. Used by Signals Overview and Signals Drilldown.
 */
export function buildSignalFilterQueryArgs(
  now: DateTime<true>,
  opts: BuildSignalFilterQueryArgsOptions,
): {
  scope: PlaybookScope;
  filters: SignalsPageFilter[];
} {
  const { scope, filters: base } = buildFilterQueryArgs(now, opts);
  const filters: SignalsPageFilter[] = [...base];

  if (opts.signalId) {
    filters.push({
      type: "signal",
      id: opts.signalId,
      metadataFilters: opts.signalMetadataFields.map((c) => c.filter),
    });
  }

  return { scope, filters };
}

export interface BuildTeamPerformancePageQueryArgsOptions extends BuildFilterQueryArgsOptions {
  /** Scopes results to a single signal when set. */
  signalId: SignalId | null;
  /** Metadata-field conditions packed as the signal filter's
   * `metadataFilters`. Ignored when `signalId` is null. */
  signalMetadataFields: readonly SignalMetadataCondition[];
  /**
   * Teammate-level coaching-goal standing filter. Optional — only the
   * by-teammate rollup consumes it; grading-level endpoints receiving the
   * bundle ignore the variant.
   */
  coachingGoal?: CoachingGoalCondition | null;
}

/**
 * Team-performance-page variant: extends the universal filters with a
 * `signal` filter (with metadata-field conditions packed as its
 * `metadataFilters`). Used by `teamPerformanceByTeammate` and
 * (eventually) the other gradingConditions consumers as they migrate.
 */
export function buildTeamPerformancePageQueryArgs(
  now: DateTime<true>,
  opts: BuildTeamPerformancePageQueryArgsOptions,
): {
  scope: PlaybookScope;
  filters: TeamPerformancePageFilter[];
} {
  const { scope } = buildFilterQueryArgs(now, opts);
  return {
    scope,
    filters: buildTeamPerformancePageFilters({
      ...opts,
      selectedDateRange: scope.dateRange,
      selectedDateRangeTimeZone: scope.timeZone,
    }),
  };
}

/**
 * Filters-only variant of {@link buildTeamPerformancePageQueryArgs} for
 * callers that don't need a {@link PlaybookScope} (e.g. the report
 * endpoints, which derive their own time window from
 * `periodEnd`/`periodLengthDays` rather than `scope.dateRange`).
 *
 * `playbookId` is nullable to support reports that aren't playbook-scoped
 * (insurance-upsell). When null, `outcome` and `playbookConditions`
 * naturally fall out — they're only kept when their `playbookId` matches.
 */
export function buildTeamPerformancePageFilters(opts: {
  playbookId: PlaybookId | null;
  teammateGroupIds: readonly GroupId[] | null;
  outcome: OutcomeCondition | null;
  customFields: readonly CustomFieldCondition[];
  totalRecordingDuration: TotalRecordingDurationCondition | null;
  recordingCount?: RecordingCountCondition | null;
  signalId: SignalId | null;
  signalMetadataFields: readonly SignalMetadataCondition[];
  playbookConditions: readonly PlaybookCondition[];
  selectedDateRange?: PlaybookScope["dateRange"];
  selectedDateRangeTimeZone?: string;
  coachingGoal?: CoachingGoalCondition | null;
}): TeamPerformancePageFilter[] {
  const filters: TeamPerformancePageFilter[] = buildUniversalFilters(opts);

  if (opts.signalId) {
    filters.push({
      type: "signal",
      id: opts.signalId,
      metadataFilters: opts.signalMetadataFields.map((c) => c.filter),
    });
  }

  if (opts.coachingGoal) {
    filters.push({
      type: "coaching_goal",
      status: opts.coachingGoal.status,
    });
  }

  return filters;
}

/**
 * Convert the potentially relative date range selected in the UI into an
 * absolute ISO date range for the API, snapped to whole-day boundaries in the
 * user's local time zone (the zone of `now`). A relative X-day range ends at
 * the end of today (partial day included) and starts at the local midnight
 * opening the X-day span, so the server's day buckets line up with the
 * user's calendar. Day-stable bounds also keep query args cache-stable all
 * day instead of refetching hourly.
 */
export function dateRangeToISORange(
  now: DateTime<true>,
  dateRange: DateRange,
): {
  after: ISODateTime;
  before: ISODateTime;
} {
  const anchor = dateRange.end ?? now;
  // Snap both bounds up to whole local days via the shared primitives (the
  // same ones the calendar/label and server-side snapping use), so an exact
  // midnight `anchor` reads as the previous day's end. Calendar subtraction
  // keeps real local midnights across DST.
  const before = endOfContainingDay(anchor);
  const after = ceilToStartOfDay(anchor.minus(dateRange.length));

  return {
    after: toISODateTime(after.toUTC()),
    before: toISODateTime(before.toUTC()),
  };
}
